import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ListCell from '@/components/ui/list/components/List.Cell';
import ListGroup from '@/components/ui/list/components/List.Group';
import { STAGGER } from '@/theme/motion';
import type { TypeListKeyboard } from '@/hooks/use.list.keyboard';
import type { TypeVirtualWindow } from '@/hooks/use.virtual.window';
import type { TypeListEntry, TypeListGroups, TypeListItemState, TypeListItemsEntry, TypeListLayout } from '@/types/list/types.list';
import type { TypeSelectionSide, TypeTableSelection } from '@/types/list/types.list.selection';

type Props<T> = {
    label: string;
    entries: TypeListEntry<T>[];
    virtual: TypeVirtualWindow;
    layout: TypeListLayout;
    columns: number;
    itemKey: (item: T) => string;
    itemLabel?: (item: T) => string;
    renderItem: (item: T, state: TypeListItemState) => React.ReactNode;
    groups?: TypeListGroups<T>;
    onToggleGroup: (group: string) => void;
    keyboard: TypeListKeyboard;
    currentKey?: string | null;
    selection?: TypeTableSelection<T>;
    selectionAt: TypeSelectionSide;
    setsizeOf: (entry: TypeListItemsEntry<T>) => number;
    onActivate?: (index: number) => void;
    onToggle?: (index: number, extend: boolean) => void;
    groupDomId: (group: string) => string;
};

type Run<T> = { key: string; group: string | null; entries: TypeListItemsEntry<T>[] };
type Block<T> = { kind: 'group'; entry: Extract<TypeListEntry<T>, { kind: 'group' }> } | { kind: 'run'; run: Run<T> };

const blocksOf = <T,>(entries: TypeListEntry<T>[]): Block<T>[] => {
    const blocks: Block<T>[] = [];
    let run: Run<T> | null = null;
    for (const entry of entries) {
        if (entry.kind === 'group') {
            run = null;
            blocks.push({ kind: 'group', entry });
            continue;
        }
        if (!run || run.group !== entry.group) {
            run = { key: `run\u0000${entry.group ?? ''}`, group: entry.group, entries: [] };
            blocks.push({ kind: 'run', run });
        }
        run.entries.push(entry);
    }
    return blocks;
};

function ListBody<T>({
    label, entries, virtual, layout, columns, itemKey, itemLabel, renderItem, groups, onToggleGroup, keyboard,
    currentKey, selection, selectionAt, setsizeOf, onActivate, onToggle, groupDomId,
}: Props<T>) {
    const shown = virtual.active ? entries.slice(virtual.start, virtual.end) : entries;
    const blocks = blocksOf(shown);
    const animated = !virtual.active;

    const { measure } = virtual;
    const measures = React.useRef(new Map<string, (node: HTMLElement | null) => void>());
    const measureOf = React.useCallback((key: string) => {
        let fn = measures.current.get(key);
        if (!fn) {
            fn = (node: HTMLElement | null) => { if (node) measure(key, node.offsetHeight); };
            measures.current.set(key, fn);
        }
        return fn;
    }, [measure]);
    const measureEntry = React.useCallback((key: string, node: HTMLElement | null) => measureOf(key)(node), [measureOf]);

    const cell = (entry: TypeListItemsEntry<T>, item: T, at: number) => {
        const key = itemKey(item);
        const index = entry.start + at;
        return (
            <ListCell
                key={key}
                item={item}
                itemKey={key}
                index={index}
                posinset={entry.offset + at + 1}
                setsize={setsizeOf(entry)}
                layout={layout.type}
                render={renderItem}
                label={itemLabel ? itemLabel(item) : undefined}
                tabIndex={keyboard.tabIndexOf(key, index)}
                active={keyboard.activeKey === key}
                current={currentKey === key}
                checked={selection ? selection.has(item) : null}
                checkLabel={selection ? selection.rowLabel(item) : undefined}
                checkAt={selectionAt}
                animated={animated}
                onActivate={onActivate}
                onToggle={onToggle}
                onKeyDown={keyboard.onKeyDown}
                onFocusKey={keyboard.onFocus}
                measureKey={entry.key}
                measure={layout.type === 'rows' ? measureEntry : undefined}
            />
        );
    };

    const renderRun = (run: Run<T>) => {
        const labelled = run.group !== null && groups
            ? { 'aria-label': groups.name ? groups.name(run.group) : run.group }
            : { 'aria-label': label };

        if (layout.type === 'grid') {
            return (
                <motion.div key={run.key} role="list" className="bd-list-run" data-layout="grid" {...labelled}
                    variants={animated ? STAGGER : undefined} initial={animated ? 'hidden' : undefined} animate={animated ? 'visible' : undefined}>
                    {run.entries.map((entry) => {
                        const span = entry.items.length < columns && entry.offset + entry.items.length === entry.size
                            ? entry.items.length
                            : columns;
                        return (
                            <div key={entry.key} ref={measureOf(entry.key)} role="none" className="bd-list-gridrow"
                                style={{ gridTemplateColumns: `repeat(${Math.max(1, span)}, minmax(0, 1fr))` }}>
                                {entry.items.map((item, at) => cell(entry, item, at))}
                            </div>
                        );
                    })}
                </motion.div>
            );
        }

        return (
            <motion.div key={run.key} role="list" className="bd-list-run" data-layout="rows" {...labelled}
                variants={animated ? STAGGER : undefined} initial={animated ? 'hidden' : undefined} animate={animated ? 'visible' : undefined}>
                <AnimatePresence>
                    {run.entries.map((entry) => cell(entry, entry.items[0], 0))}
                </AnimatePresence>
            </motion.div>
        );
    };

    return (
        <>
            {virtual.padTop > 0 ? <div aria-hidden style={{ height: virtual.padTop }} /> : null}
            {blocks.map((block) => (block.kind === 'group' ? (
                <ListGroup
                    key={block.entry.key}
                    id={groupDomId(block.entry.group)}
                    label={groups ? groups.label(block.entry.group, block.entry.items) : block.entry.group}
                    count={block.entry.count}
                    collapsed={block.entry.collapsed}
                    collapsible={Boolean(groups?.collapsible)}
                    sticky={Boolean(groups?.sticky)}
                    onToggle={() => onToggleGroup(block.entry.group)}
                    measure={measureOf(block.entry.key)}
                />
            ) : renderRun(block.run)))}
            {virtual.padBottom > 0 ? <div aria-hidden style={{ height: virtual.padBottom }} /> : null}
        </>
    );
}

export default ListBody;
